import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Logger } from 'src/utils/Logger';
import { ApiError } from 'src/error/ApiError';
import PreShareWarningModal from './PreShareWarningModal';
import useShareTenantSkill from './hooks/useShareTenantSkill';
import type { ShareOutcome } from './types';

type ShareSkillButtonProps = {
  skillId: string;
  skillLabel: string;
  isShared: boolean;
};

const SUCCESS_NOTIFICATIONS: Record<
  ShareOutcome,
  (skillLabel: string) => { message: string; color: string }
> = {
  FIRST_SHARE: skillLabel => ({ message: `Shared '${skillLabel}' with everyone`, color: 'green' }),
  UPDATE: skillLabel => ({ message: `Published an update to '${skillLabel}'`, color: 'green' }),
  UNCHANGED: skillLabel => ({
    message: `'${skillLabel}' is already up to date — nothing to publish`,
    color: 'blue',
  }),
};

const FALLBACK_NOTIFICATION = (skillLabel: string) => ({
  message: `Shared '${skillLabel}'`,
  color: 'green',
});

export default function ShareSkillButton({ skillId, skillLabel, isShared }: ShareSkillButtonProps) {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  // Fixed when the dialog opens: a first share refetches the row as shared before the dialog
  // closes, and the open dialog must not turn into an update mid-request.
  const [isUpdate, setIsUpdate] = useState(isShared);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync, isPending } = useShareTenantSkill();

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      const { outcome } = await mutateAsync(skillId);
      setIsWarningOpen(false);
      // An API older than this website sends no outcome; the share still worked.
      const notify = Object.hasOwn(SUCCESS_NOTIFICATIONS, outcome)
        ? SUCCESS_NOTIFICATIONS[outcome]
        : FALLBACK_NOTIFICATION;
      notifications.show(notify(skillLabel));
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'An error occurred';
      setErrorMessage(message);
      // The modal stays open on failure, so the inline alert is the one place the user reads
      // this — a toast as well would be the same sentence twice. A 409 is the API refusing on
      // purpose because the skill still matches the library entry it was added from, so it is a
      // warning rather than a fault worth an exception in Sentry.
      if (error instanceof ApiError && error.status === 409) {
        Logger.warn(`Could not share skill: ${message}`);
      } else {
        Logger.error(`Could not share skill: ${message}`, error);
      }
    }
  };

  const warningTitle = isUpdate
    ? `Publish an update to '${skillLabel}'?`
    : `Share '${skillLabel}' with everyone on Afternoon?`;
  const confirmLabel = isUpdate ? 'Publish update' : 'Share with everyone';

  return (
    <>
      <Button
        variant="default"
        size="compact-sm"
        aria-label={isShared ? `Publish update to ${skillLabel}` : `Share ${skillLabel}`}
        onClick={() => {
          setIsUpdate(isShared);
          setIsWarningOpen(true);
        }}
      >
        {isShared ? 'Publish update' : 'Share'}
      </Button>
      <PreShareWarningModal
        opened={isWarningOpen}
        title={warningTitle}
        confirmLabel={confirmLabel}
        isSharing={isPending}
        errorMessage={errorMessage}
        onClose={() => {
          setErrorMessage(null);
          setIsWarningOpen(false);
        }}
        onConfirm={handleConfirm}
      />
    </>
  );
}
